'use client'

import type { TFormField } from '@/components/project/ai-chat/editor/_types'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProjectStore } from '@/hooks/use-project-store'
import { addFormField, updateFormField } from '@/lib/editor/form-config'
import {
  CalendarIcon,
  CheckLineIcon,
  FileIcon,
  HashIcon,
  LinkIcon,
  MailIcon,
  MousePointerIcon,
  PhoneIcon,
  TextIcon,
  ToggleLeftIcon,
  TypeIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

interface FormFieldDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schemaId: string
  onSuccess: (updatedTree?: any) => void
  field?: TFormField // If provided, we're editing; if not, we're adding
  mode?: 'add' | 'edit' // Explicit mode for clarity
}

export function FormFieldDialog({
  open,
  onOpenChange,
  schemaId,
  onSuccess,
  field,
  mode = field ? 'edit' : 'add',
}: FormFieldDialogProps) {
  const isEditMode = mode === 'edit'
  const t = useTranslations('form-custom-field-dialog')
  const { project, setProject } = useProjectStore()

  const [fieldData, setFieldData] = useState<Omit<TFormField, 'id'>>({
    label: field?.label || '',
    type: field?.type || 'text',
    required: field?.required || false,
    placeholder: field?.placeholder || '',
    options: field?.options || [],
  })

  const [optionInput, setOptionInput] = useState('')
  const [validationError, setValidationError] = useState('')

  // Reset field data when field prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setFieldData({
        label: field?.label || '',
        type: field?.type || 'text',
        required: field?.required || false,
        placeholder: field?.placeholder || '',
        options: field?.options || [],
      })
      setOptionInput('')
    }
  }, [field, open])

  const cname = 'size-4'
  const fieldTypes = [
    {
      value: 'text',
      label: t('field-types.text'),
      icon: <TypeIcon className={cname} />,
    },
    {
      value: 'email',
      label: t('field-types.email'),
      icon: <MailIcon className={cname} />,
    },
    {
      value: 'phone',
      label: t('field-types.phone'),
      icon: <PhoneIcon className={cname} />,
    },
    {
      value: 'number',
      label: t('field-types.number'),
      icon: <HashIcon className={cname} />,
    },
    {
      value: 'date',
      label: t('field-types.date'),
      icon: <CalendarIcon className={cname} />,
    },
    {
      value: 'boolean',
      label: t('field-types.boolean'),
      icon: <ToggleLeftIcon className={cname} />,
    },
    {
      value: 'checkbox',
      label: t('field-types.checkbox'),
      icon: <CheckLineIcon className={cname} />,
    },
    {
      value: 'select',
      label: t('field-types.select'),
      icon: <MousePointerIcon className={cname} />,
    },
    // 麻烦谁有时间兼容一下
    // {
    //   value: 'file',
    //   label: t('field-types.file'),
    //   icon: <FileIcon className={cname} />,
    // },
    {
      value: 'web_url',
      label: t('field-types.web_url'),
      icon: <LinkIcon className={cname} />,
    },
  ]

  const needsOptions = ['select'].includes(fieldData.type)

  // Validation functions
  const validatePhoneNumber = (phone: string) => {
    if (!phone.trim()) return true // Empty is valid (will be caught by required check)
    // Basic phone validation - allows various formats
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''))
  }

  const validateWebUrl = (url: string) => {
    if (!url.trim()) return true // Empty is valid (will be caught by required check)

    const trimmedUrl = url.trim()

    // Reject invalid characters and patterns
    if (trimmedUrl.includes('@') || trimmedUrl.includes(' ')) {
      return false
    }

    // Don't allow just protocols or incomplete protocols
    if (
      trimmedUrl === 'https://' ||
      trimmedUrl === 'http://' ||
      trimmedUrl === 'https://www' ||
      trimmedUrl === 'http://www' ||
      trimmedUrl.endsWith('://') ||
      trimmedUrl.endsWith('://www')
    ) {
      return false
    }

    // If it has protocol, validate the complete URL
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(trimmedUrl)
        // Must be https, have a valid hostname with at least one dot, and no invalid characters
        return (
          urlObj.protocol === 'https:' &&
          urlObj.hostname.length > 0 &&
          urlObj.hostname.includes('.') &&
          !urlObj.hostname.startsWith('.') &&
          !urlObj.hostname.endsWith('.') &&
          /^[a-zA-Z0-9.-]+$/.test(urlObj.hostname)
        )
      } catch {
        return false
      }
    }

    // If no protocol, validate as domain only
    const domainPattern =
      /^(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/
    return domainPattern.test(trimmedUrl)
  }

  const validateField = (type: string, value: string) => {
    switch (type) {
      case 'phone':
        if (value && !validatePhoneNumber(value)) {
          return 'Please enter a valid phone number (digits only, optional country code)'
        }
        break
      case 'web_url':
        if (value && !validateWebUrl(value)) {
          return 'Please enter a valid website URL (e.g., www.example.com or example.com)'
        }
        break
    }
    return ''
  }

  const handleAddOption = () => {
    if (
      optionInput.trim() &&
      !fieldData.options?.includes(optionInput.trim())
    ) {
      setFieldData({
        ...fieldData,
        options: [...(fieldData.options || []), optionInput.trim()],
      })
      setOptionInput('')
    }
  }

  const handleRemoveOption = (index: number) => {
    setFieldData({
      ...fieldData,
      options: fieldData.options?.filter((_, i) => i !== index) || [],
    })
  }

  const handleSave = () => {
    if (!fieldData.label.trim() || !project?.file_system_tree) return

    // Validate options for fields that need them
    if (
      needsOptions &&
      (!fieldData.options || fieldData.options.length === 0)
    ) {
      return
    }

    // Validate field-specific requirements
    const error = validateField(fieldData.type, fieldData.placeholder || '')
    if (error) {
      setValidationError(error)
      return
    }

    const updatedTree =
      isEditMode && field
        ? updateFormField(
            project.file_system_tree,
            schemaId,
            field.id,
            fieldData,
          )
        : addFormField(project.file_system_tree, schemaId, fieldData)

    setProject({
      ...project,
      file_system_tree: updatedTree,
    })

    onSuccess(updatedTree)
  }

  const handleClose = () => {
    // Reset form when closing
    setFieldData({
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    })
    setOptionInput('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {isEditMode ? t('edit.title') : t('add.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Field Label */}
          <div className="space-y-2">
            <Label htmlFor="field-label" className="text-white">
              {t('field-label')} *
            </Label>
            <Input
              id="field-label"
              value={fieldData.label}
              onChange={(e) =>
                setFieldData({ ...fieldData, label: e.target.value })
              }
              placeholder={t('field-label-placeholder')}
              className="bg-white/10 border-gray-600 text-white"
            />
          </div>

          {/* Field Type */}
          <div className="space-y-2">
            <Label htmlFor="field-type" className="text-white">
              {t('field-type')}
            </Label>
            <Select
              value={fieldData.type}
              onValueChange={(value: TFormField['type']) => {
                setFieldData({ ...fieldData, type: value, options: [] })
                setValidationError('') // Clear validation error when changing type
              }}
            >
              <SelectTrigger className="bg-white/10 border-gray-600 text-white">
                <SelectValue placeholder={t('select-field-type')} />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-gray-600 z-[105]">
                {fieldTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      {type.icon}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Field Placeholder */}
          <div className="space-y-2">
            <Label htmlFor="field-placeholder" className="text-white">
              {t('field-placeholder')}
            </Label>
            <Input
              id="field-placeholder"
              value={fieldData.placeholder}
              onChange={(e) => {
                const newValue = e.target.value
                setFieldData({ ...fieldData, placeholder: newValue })
                // Validate placeholder for phone/web_url fields
                const error = validateField(fieldData.type, newValue)
                setValidationError(error)
              }}
              placeholder={t('field-placeholder-placeholder')}
              className="bg-white/10 border-gray-600 text-white"
            />
            {validationError && (
              <p className="text-red-400 text-sm mt-1">{validationError}</p>
            )}
          </div>

          {/* Required Field Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="field-required"
              checked={fieldData.required}
              onCheckedChange={(checked) =>
                setFieldData({ ...fieldData, required: checked === true })
              }
              className="border-gray-600"
            />
            <Label htmlFor="field-required" className="text-white">
              {t('field-required')}
            </Label>
          </div>

          {/* Options Section */}
          {needsOptions && (
            <div className="space-y-2">
              <Label className="text-white">{t('field-options')}</Label>
              <div className="flex gap-2">
                <Input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  placeholder={t('add-option-placeholder')}
                  className="bg-white/10 border-gray-600 text-white flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddOption()
                    }
                  }}
                />
                <Button type="button" onClick={handleAddOption}>
                  {t('add-option')}
                </Button>
              </div>

              {/* Options List */}
              {fieldData.options && fieldData.options.length > 0 && (
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {fieldData.options.map((option, index) => (
                    <div
                      key={`option-${option}-${index}`}
                      className="flex items-center justify-between p-2 bg-white/5 rounded"
                    >
                      <span className="text-white">{option}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOption(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="transparent" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button
              variant={'active'}
              onClick={handleSave}
              disabled={
                !fieldData.label.trim() ||
                (needsOptions &&
                  (!fieldData.options || fieldData.options.length === 0)) ||
                !!validationError
              }
            >
              {t('save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
